'use client';

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { User } from '@provaplus/shared';
import * as authApi from '@/lib/auth.api';
import { supabase } from '@/lib/supabase';

interface AuthContextData {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const storedUser = authApi.getStoredUser();
      const storedToken = authApi.getStoredToken();
      
      if (storedUser && storedToken) {
        setUser(storedUser);
        
        // Autenticar Supabase client com o token JWT
        await supabase.auth.setSession({
          access_token: storedToken,
          refresh_token: storedToken,
        });
        
        // Validar e atualizar perfil em background
        const freshUser = await authApi.getProfile();
        if (freshUser) {
          setUser(freshUser);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar usuário:', error);
    } finally {
      setLoading(false);
    }
  }

  async function refreshUser() {
    // Buscar perfil do backend
    setLoading(true);
    try {
      const freshUser = await authApi.getProfile();
      if (freshUser) {
        setUser(freshUser);
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignIn(email: string, password: string) {
    const result = await authApi.login({ email, password });
    
    // Autenticar Supabase client com o token JWT
    await supabase.auth.setSession({
      access_token: result.session.access_token,
      refresh_token: result.session.access_token,
    });
    
    // Buscar perfil atualizado do backend para garantir dados corretos
    const freshUser = await authApi.getProfile();
    setUser(freshUser || result.user);
  }

  async function handleSignUp(email: string, password: string, name: string) {
    const result = await authApi.register({ email, password, name });
    setUser(result.user);
  }

  async function handleSignOut() {
    authApi.logout();
    await supabase.auth.signOut();
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin,
        signIn: handleSignIn,
        signUp: handleSignUp,
        signOut: handleSignOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
